/**
 * useOnsets - what each figure has just begun to wear, by figure (plan design-gaps, S2 and S4).
 *
 * Derived per screen from what it draws: its standing states (engine/display/states; the figure draws a new one at
 * twice the size, settling) and its posture (S4: it topples as it falls, turns back up as it stands). Anything a
 * figure wears now and did not a render ago has its ONSET for ONSET_MS - each figure on its own clock - then it
 * simply stands. A figure seen for the
 * first time has no onsets (a screen joining mid-fight does not replay them), and a screen that holds still gets none.
 *
 * @param {Record<string, readonly { id: string }[]>} states - what each figure wears, by token id
 * @param {boolean} still - the viewer's motion setting
 * @param {number} [ms] - how long an onset lasts (ONSET_MS; a plate's arm moment runs a second)
 * @returns {Record<string, string[]>} the state ids in onset, by token id
 */
import { useEffect, useMemo, useRef, useState } from 'react';

export const ONSET_MS = 600;
const NONE = Object.freeze({});

/** The states each figure has gained since `before`; a figure `before` never held has gained nothing. */
export function gainedStates(before, now) {
  const out = {};
  for (const [id, states] of Object.entries(now)) {
    const was = before[id];
    if (!was) continue;
    const had = new Set(was.map((s) => s.id));
    const gained = states.filter((s) => !had.has(s.id)).map((s) => s.id);
    if (gained.length) out[id] = gained;
  }
  return out;
}

export default function useOnsets(states, still, ms = ONSET_MS) {
  const before = useRef(null);
  const [onsets, setOnsets] = useState(NONE);
  const expiry = useExpiry(setOnsets);
  useEffect(() => {
    const gained = before.current ? gainedStates(before.current, states) : {};
    before.current = states;
    if (still || !Object.keys(gained).length) return;
    // MERGED, each figure on its own clock (the Track T simplify): a walker stepping into a surface must not wipe
    // another figure's ring that began a moment before. A new gain for a figure restarts that figure's alone.
    setOnsets((o) => ({ ...o, ...gained }));
    expiry.start(Object.keys(gained), ms);
  }, [states, still, ms, expiry]);
  return still ? NONE : onsets;
}

/**
 * Each figure's onset end, and the one timer that clears the ended ones - it outlives the effect's re-runs (`states`
 * is rebuilt on every roster echo, and a re-run that gained nothing must not cut an onset short: the Track S review).
 */
function useExpiry(setOnsets) {
  const until = useRef({});
  const timer = useRef(undefined);
  const expiry = useMemo(() => {
    const sweep = () => {
      const now = Date.now();
      until.current = Object.fromEntries(Object.entries(until.current).filter(([, end]) => end > now));
      const left = Object.keys(until.current);
      setOnsets((o) => (left.length ? Object.fromEntries(left.filter((id) => o[id]).map((id) => [id, o[id]])) : NONE));
      if (left.length) timer.current = setTimeout(sweep, Math.min(...Object.values(until.current)) - now);
    };
    return {
      start(ids, ms) {
        const end = Date.now() + ms;
        for (const id of ids) until.current[id] = end;
        clearTimeout(timer.current);
        timer.current = setTimeout(sweep, Math.min(...Object.values(until.current)) - Date.now());
      },
    };
  }, [setOnsets]);
  useEffect(() => () => clearTimeout(timer.current), []);
  return expiry;
}
